/* Sign-contract modal: canvas glyph + OTP + typed name. Opened by
 * MyContractsCard when the author clicks "Sign as author".
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ShieldCheck, MailCheck, Loader2 } from 'lucide-react';
import SignatureCanvas from './SignatureCanvas';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function SignContractDialog({ contract, onClose, onSigned }) {
  const [name, setName] = useState('');
  const [glyph, setGlyph] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!contract) return null;

  const requestOtp = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/authors/me/contracts/id/${contract.id}/otp/request`);
      setOtpSent(true);
      toast.success(`Code sent to ${r.data.sent_to}. Expires in 10 min.`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send OTP.'); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!name || !glyph || !otp || !consent) {
      toast.error('Please complete name, glyph, OTP, and consent.');
      return;
    }
    setBusy(true);
    try {
      const r = await http.post(`/authors/me/contracts/id/${contract.id}/sign`, {
        signer_name: name,
        signature_data_url: glyph,
        otp_code: otp,
        consent: true,
      });
      toast.success(`Signed. Contract status: ${r.data.status}`);
      onSigned?.();
      onClose?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Sign failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="sign-contract-dialog">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-4 shadow-xl">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[14px] font-semibold">Sign contract · {contract.template_title}</div>
          <button type="button" onClick={onClose} data-testid="sign-dialog-close" className="rounded-sm p-1 hover:bg-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[11.5px] text-muted-foreground">
          Signing this contract is legally binding. Your name, drawn glyph,
          email OTP, IP, and timestamp are captured under a SHA-256 tamper seal.
        </p>

        <label className="block">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Your legal name</div>
          <input
            type="text"
            className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            value={name}
            data-testid="sign-name-input"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div className="mt-3">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Draw your signature</div>
          <SignatureCanvas onCapture={(url) => { setGlyph(url); toast.success('Signature captured.'); }} />
        {glyph && (
          <div className="mt-2 rounded-md border border-emerald-500/40 bg-white p-2" data-testid="sign-glyph-preview">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-500">
              Signature preview
            </div>
            <img
              src={glyph}
              alt="Your captured signature"
              className="max-h-24"
              data-testid="sign-glyph-preview-img"
            />
            <div className="mt-1 text-[10px] text-muted-foreground">
              Weight: {Math.round(glyph.length / 1024)} KB · Redraw above and click "Use this signature" to update.
            </div>
          </div>
        )}
        </div>

        <div className="mt-3 rounded-md border border-border bg-panel-header/40 p-2">
          <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold">
            <MailCheck className="h-3 w-3" /> Email OTP
          </div>
          {!otpSent ? (
            <button
              type="button" onClick={requestOtp} disabled={busy}
              data-testid="sign-request-otp"
              className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MailCheck className="h-3 w-3" />}
              Send code to my email
            </button>
          ) : (
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              className="h-8 w-40 rounded-sm border border-border bg-background px-2 text-[12px] tracking-widest"
              value={otp}
              data-testid="sign-otp-input"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            />
          )}
        </div>

        <label className="mt-3 flex items-start gap-2 text-[11px]">
          <input type="checkbox" checked={consent}
                 data-testid="sign-consent"
                 onChange={(e) => setConsent(e.target.checked)} />
          <span>
            I intend my typed name and drawn glyph to be my electronic
            signature, and I consent to the recording of my IP address
            and OTP confirmation as part of the tamper-seal.
          </span>
        </label>

        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            data-testid="sign-cancel"
            className="rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !consent || !name || !glyph || !otp}
            data-testid="sign-submit"
            className="inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <ShieldCheck className="h-4 w-4" /> Sign contract
          </button>
        </div>
      </div>
    </div>
  );
}
